import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeftRight, ArrowRight, Package, Plus, X as IconX, Trash2, Search, GitBranch, Warehouse, RotateCcw, Truck as TruckIcon, MoreVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { VEHICLE_OPTIONS } from '../../components/DeliveryVehicleIcon';
import { CustomSelect } from '../../components/CustomSelect';
import { FilterPopover } from '../../components/FilterPopover';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import {
  catalogProductsOf, CENTRAL_LOCATION,
  TRANSFER_STAGES, TRANSFER_STAGE_META, stockLocationsOf, locationLabelOf,
  stockTransfersOf, addStockTransfer, updateStockTransfer, deleteStockTransfer,
  advanceTransfer, nextTransferStage, isModuleOn, refreshTransport, stockRowsOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { useWebSocket } from '../../hooks/useWebSocket';

// The three routed flows a stock transfer can use. `needsWarehouse: true`
// means the route is only available to orgs whose plan includes the warehouse
// module — otherwise it's hidden from the route picker.
const ROUTE_OPTIONS = [
  { id: 'wh-to-wh', label: 'Warehouse → Warehouse', desc: 'Warehouse-to-warehouse stock relocation.', needsWarehouse: true },
  { id: 'wh-to-branch', label: 'Warehouse → Branch', desc: 'Routed dispatch from a warehouse to a branch.', needsWarehouse: true },
  { id: 'branch-to-wh', label: 'Branch → Warehouse', desc: 'Return aging or unsold stock back to a warehouse.', needsWarehouse: true },
  { id: 'branch-to-branch', label: 'Branch → Branch', desc: 'Directly transfer stock between branches.', needsWarehouse: false },
];

// Classify a transfer by its from/to kinds. Returns one of the route ids or
// null if it doesn't fit any (e.g. unknown locations).
const routeOf = (t, byId) => {
  const f = byId[t.fromLocationId]?.kind;
  const u = byId[t.toLocationId]?.kind;
  if (f === 'warehouse' && u === 'branch') return 'wh-to-branch';
  if (f === 'branch' && u === 'warehouse') return 'branch-to-wh';
  if (f === 'warehouse' && u === 'warehouse') return 'wh-to-wh';
  if (f === 'branch' && u === 'branch') return 'branch-to-branch';
  return null;
};

const today = () => new Date().toISOString().slice(0, 10);

// How a transfer is named to a person.
//
// The messages used to quote the row's own id — "Transfer tr-mtdx47pr-wsa7
// moved to Approved" — which is a database key, not a name: unreadable on
// screen, impossible to say over a phone, and it tells the reader nothing they
// were not already looking at. The route is what identifies a transfer to the
// people moving it, and it is what the Delivery tab already does with the
// customer's name.
const transferLabel = (t) =>
  `${locationLabelOf(t.fromLocationId)} → ${locationLabelOf(t.toLocationId)}`;

const LocationPill = ({ id }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0d0d0d] min-w-0">
    {id === CENTRAL_LOCATION
      ? <Warehouse size={12} className="shrink-0 text-slate-400" aria-hidden="true" />
      : <GitBranch size={12} className="shrink-0 text-slate-400" aria-hidden="true" />}
    <span className="truncate">{locationLabelOf(id)}</span>
  </span>
);

LocationPill.propTypes = { id: PropTypes.string.isRequired };

// ---- Carrier modal ----
const CarrierModal = ({ transfer, accent, onClose, onSave }) => {
  const [carrier, setCarrier] = useState(transfer.carrier || '');
  const [vehicle, setVehicle] = useState(transfer.vehicle || 'Van');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden"
        >
          <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
            <div>
              <h3 className="text-base font-extrabold text-[#0d0d0d]">Carrier &amp; vehicle</h3>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{transfer.id}</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
              <IconX size={16} />
            </button>
          </header>
          <div className="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block space-y-1 sm:col-span-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Carrier name</span>
              <input type="text" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. Theo Marsh"
                className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Vehicle</span>
              <CustomSelect
                value={vehicle}
                onChange={setVehicle}
                options={VEHICLE_OPTIONS.map((v) => ({ value: v, label: v }))}
                className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
              />
            </label>
          </div>
          <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
            <button type="button" onClick={() => onSave({ carrier: carrier.trim(), vehicle: carrier.trim() ? vehicle : '' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}>
              Save carrier
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

CarrierModal.propTypes = {
  transfer: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ---- View transfer modal ----
const ViewTransferModal = ({ transfer, accent, onClose }) => {
  const meta = TRANSFER_STAGE_META[transfer.status] || { label: transfer.status, color: '#64748b' };
  const totalUnits = (transfer.lines || []).reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const formatDate = (val) => {
    if (!val) return null;
    try { return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(val)); }
    catch { return val; }
  };
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}>
        <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col max-h-[90vh]">
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-start justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: accent }}>
                <ArrowLeftRight size={18} />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-[#0d0d0d]">Transfer Details</h3>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{transfer.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="inline-flex items-center text-[12px] font-extrabold uppercase tracking-widest"
                style={{ color: meta.color }}>
                {meta.label}
              </span>
              <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <IconX size={18} />
              </button>
            </div>
          </header>
          <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex-1 flex flex-col gap-5">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-3 px-1">Route</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-white border border-slate-200 shadow-sm rounded-lg px-4 py-2.5 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">From</p>
                  <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{locationLabelOf(transfer.fromLocationId)}</p>
                </div>
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 border border-slate-200 shrink-0 text-slate-400">
                  <ArrowRight size={14} />
                </div>
                <div className="flex-1 bg-white border border-slate-200 shadow-sm rounded-lg px-4 py-2.5 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">To</p>
                  <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{locationLabelOf(transfer.toLocationId)}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-3 px-1">Logistics</p>
              <div className="bg-white border border-slate-200 shadow-sm rounded-lg divide-y divide-slate-100">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="px-4 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Carrier</p>
                    <p className="text-[12px] font-extrabold text-[#0d0d0d] truncate">{transfer.carrier || '-'}</p>
                  </div>
                  <div className="px-4 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Vehicle</p>
                    <p className="text-[12px] font-extrabold text-[#0d0d0d] truncate">{transfer.vehicle || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="px-4 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Created</p>
                    <p className="text-[12px] font-extrabold text-[#0d0d0d] truncate">{formatDate(transfer.createdAt) || '-'}</p>
                  </div>
                  <div className="px-4 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Expected Delivery</p>
                    <p className="text-[12px] font-extrabold text-[#0d0d0d] truncate">{formatDate(transfer.expectedDate) || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400">Products</p>
                <span className="text-[11px] font-bold text-slate-500">{totalUnits} total units</span>
              </div>
              <div className="bg-white border border-slate-200 shadow-sm rounded-lg overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-50/50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-2.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Product</th>
                      <th className="px-4 py-2.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Variant</th>
                      <th className="px-4 py-2.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(transfer.lines || []).map((l, i) => (
                      <tr key={i} className="hover:bg-slate-50/80 even:bg-slate-50/40 transition-colors">
                        <td className="px-4 py-3 text-xs font-bold text-slate-800">{l.name}</td>
                        <td className="px-4 py-3 text-xs font-medium text-slate-500">{l.label && l.label !== 'Default' ? l.label : 'N/A'}</td>
                        <td className="px-4 py-3 text-right text-xs font-extrabold text-[#0d0d0d]">{l.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-wider shadow-sm hover:shadow transition-all" style={{ background: accent }}>Close</button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ---- New transfer modal ----
const NewTransferModal = ({ org, fromLocations, toLocations, defaultFromId, accent, onClose, onCreate }) => {
  const allowedFromId = (id) => fromLocations.some((l) => l.id === id) ? id : (fromLocations[0]?.id || '');
  const firstFrom = allowedFromId(defaultFromId || fromLocations[0]?.id || '');
  const [fromId, setFromId] = useState(firstFrom);
  const [toId, setToId] = useState(toLocations.find((l) => l.id !== firstFrom)?.id || toLocations[0]?.id || '');
  const [carrier, setCarrier] = useState('');
  const [vehicle, setVehicle] = useState('Van');
  const [expectedDate, setExpectedDate] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([{ productId: '', variantId: '', qty: '1' }]);

  const allProducts = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const activeStock = useMemo(() => stockRowsOf(org.id, fromId, 'active'), [org.id, fromId]);
  
  const inStockProductIds = useMemo(() => new Set(activeStock.filter(r => r.qty > 0).map(r => r.productId)), [activeStock]);
  const products = useMemo(() => allProducts.filter(p => inStockProductIds.has(p.id)), [allProducts, inStockProductIds]);
  const productById = useMemo(() => Object.fromEntries(allProducts.map((p) => [p.id, p])), [allProducts]);

  const changeFrom = (id) => {
    setFromId(id);
    if (id === toId) setToId(toLocations.find((l) => l.id !== id)?.id || toLocations[0]?.id || '');
    setLines([{ productId: '', variantId: '', qty: '1' }]);
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { productId: '', variantId: '', qty: '1' }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const changeProduct = (i, productId) => {
    const inStockVariants = activeStock.filter(r => r.productId === productId && r.qty > 0).map(r => r.variantId);
    const firstInStockVariant = inStockVariants.length > 0 ? inStockVariants[0] : '';
    setLine(i, { productId, variantId: firstInStockVariant });
  };

  const validLines = lines
    .map((l) => {
      const product = productById[l.productId];
      if (!product) return null;
      const variant = (product.variants || []).find((v) => v.id === l.variantId);
      return {
        productId: product.id,
        variantId: l.variantId || '',
        name: product.name,
        label: variant ? variant.label : '',
        qty: Number(l.qty) || 0,
      };
    })
    .filter((l) => l && l.qty > 0);
  const totalUnits = validLines.reduce((s, l) => s + l.qty, 0);
  const canSubmit = fromId && toId && fromId !== toId && validLines.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onCreate({
      orgId: org.id,
      fromLocationId: fromId,
      toLocationId: toId,
      lines: validLines,
      carrier: carrier.trim(),
      vehicle: carrier.trim() ? vehicle : '',
      note: note.trim(),
      expectedDate: expectedDate || null,
      status: 'requested',
      createdAt: today(),
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col"
          style={{ maxHeight: '92vh' }}
        >
          <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }} aria-hidden="true">
                <ArrowLeftRight size={18} />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-[#0d0d0d]">New stock transfer</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Move goods between locations</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
              <IconX size={16} />
            </button>
          </header>

          <div className="px-5 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-4">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-3 px-1">Route</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-white border border-slate-200 shadow-sm rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">From</p>
                  <CustomSelect
                    value={fromId}
                    onChange={changeFrom}
                    options={fromLocations.map((l) => ({ value: l.id, label: l.name }))}
                    disabled={!!defaultFromId}
                    className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
                  />
                </div>
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 border border-slate-200 shrink-0 text-slate-400">
                  <ArrowRight size={14} />
                </div>
                <div className="flex-1 bg-white border border-slate-200 shadow-sm rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">To</p>
                  <CustomSelect
                    value={toId}
                    onChange={setToId}
                    options={toLocations.filter((l) => l.id !== fromId).map((l) => ({ value: l.id, label: l.name }))}
                    className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Items to move</span>
                <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--theme-primary)]">
                  <Plus size={12} /> Add item
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {lines.map((line, i) => {
                  const product = productById[line.productId];
                  let variants = product?.variants || [];
                  const inStockVariants = activeStock.filter(r => r.productId === line.productId && r.qty > 0).map(r => r.variantId);
                  
                  // If product has variants, only show those in stock
                  if (variants.length > 0) {
                    variants = variants.filter(v => inStockVariants.includes(v.id));
                  }
                  
                  // Find available qty for the selected item/variant
                  const availableStockRow = activeStock.find(r => r.productId === line.productId && r.variantId === (line.variantId || ''));
                  const maxQty = availableStockRow ? availableStockRow.qty : 0;

                  return (
                    // Descending z-index keeps an open dropdown above the rows
                    // (and sections) beneath it inside the scrollable modal body.
                    <div key={i} className="relative flex flex-col gap-1 mb-2" style={{ zIndex: 50 - i }}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <CustomSelect
                            value={line.productId}
                            onChange={(v) => changeProduct(i, v)}
                            options={products.map((p) => ({ value: p.id, label: p.name }))}
                            placeholder="Select item"
                            className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
                          />
                        </div>
                        <div className="w-32 shrink-0">
                          <CustomSelect
                            value={line.variantId}
                            onChange={(v) => setLine(i, { variantId: v })}
                            options={variants.length
                              ? variants.map((v) => ({ value: v.id, label: v.label }))
                              : [{ value: '', label: 'Standard' }]}
                            disabled={!product || variants.length === 0}
                            placeholder="Variant"
                            className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-2"
                          />
                        </div>
                        <input type="number" min="1" max={maxQty} value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} placeholder="Qty" aria-label="Quantity"
                          className="w-16 shrink-0 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d] text-center" />
                        <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} aria-label="Remove item"
                          className="p-1.5 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30 shrink-0">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {line.productId && (
                        <div className="text-[10px] text-slate-500 pl-1 font-medium">
                          Available in stock: <span className="font-bold text-slate-700">{maxQty}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-3 px-1">Logistics</p>
              <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <label className="block">
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">Carrier</span>
                    <input type="text" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier name"
                      className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">Vehicle</span>
                    <CustomSelect
                      value={vehicle}
                      onChange={setVehicle}
                      options={VEHICLE_OPTIONS.map((v) => ({ value: v, label: v }))}
                      className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">Expected Date</span>
                    <CustomDatePickerFixed
                      value={expectedDate}
                      onChange={setExpectedDate}
                      className="w-full"
                    />
                  </label>
                </div>
              </div>
            </div>

            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Note (optional)</span>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Weekly branch restock"
                className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
          </div>

          <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-between gap-3" style={{ background: 'var(--theme-secondary)' }}>
            <div className="text-[11px] font-bold text-slate-500">
              {validLines.length} item{validLines.length === 1 ? '' : 's'} · <span className="text-[#0d0d0d]">{totalUnits} units</span>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
              <button type="button" onClick={submit} disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-50"
                style={{ background: accent }}>
                Create transfer
              </button>
            </div>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

NewTransferModal.propTypes = {
  org: PropTypes.object.isRequired,
  fromLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  toLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultFromId: PropTypes.string,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

// ---- Transfer card ----
// Every value on a card carries one of these, so the reader never has to infer
// what they are looking at.
const FieldLabel = ({ children }) => (
  <p className="mb-0.5 truncate text-[9px] font-bold uppercase leading-none tracking-wider text-slate-400">
    {children}
  </p>
);

FieldLabel.propTypes = { children: PropTypes.node.isRequired };

const TransferCard = ({ transfer, accent, onAdvance, onCancel, onCarrier, onReopen, onDelete, onView }) => {
  const meta = TRANSFER_STAGE_META[transfer.status];
  const next = nextTransferStage(transfer.status);
  const cancelled = transfer.status === 'cancelled';
  const units = (transfer.lines || []).reduce((s, l) => s + l.qty, 0);

  const noteText = (transfer.note || '').toLowerCase();
  const emergency = noteText.includes('emerg') || noteText.includes('emery');

  // A transfer cannot be dragged onward until somebody is carrying it — the
  // `draggable` test below has always said so. The card never showed it, so a
  // card that would not move gave no reason why, and the only way to find out
  // was to open the menu. It is a field now.
  const carrier = (transfer.carrier || '').trim();

  // A transfer carries two dates: when it was raised, and when it is expected to
  // arrive — which the details modal calls "Created" and "Expected Delivery".
  // The card has room for one, and the earlier version simply headed it "Date",
  // which named neither and left the reader guessing which they were looking at.
  //
  // The expected date is what somebody waiting on stock actually wants, so it
  // wins when it is set. It is optional, though, so when it is missing the card
  // falls back to the created date — and the heading changes with it, so the
  // figure is always named.
  const formatStamp = (value) => {
    const at = new Date(value);
    if (!value || Number.isNaN(at.getTime())) return null;
    return {
      short: new Intl.DateTimeFormat('en-IN', { month: 'short', day: 'numeric' }).format(at).toUpperCase(),
      full: new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(at),
    };
  };

  const expected = formatStamp(transfer.expectedDate);
  const created = formatStamp(transfer.createdAt);
  const stamp = expected
    ? { label: 'Expected Date', value: expected, muted: false }
    : { label: 'Created Date', value: created, muted: true };

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // A menu opened by tapping has to be closable by tapping elsewhere: a touch
  // screen has no "move the pointer away". It used to open on hover only, so on
  // a tablet it either did nothing or stuck open with nothing to dismiss it.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const runAction = (fn) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn();
  };

  const menuItem = 'flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-medium transition-colors';

  return (
    <motion.article layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      draggable={!cancelled && !!carrier}
      onDragStart={(e) => {
        if (cancelled || !carrier) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData('transferId', transfer.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      className="bg-white border border-[var(--theme-tertiary)] rounded-md p-2.5 flex flex-col gap-2 relative hover:z-20 group shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all w-full min-w-0 cursor-grab active:cursor-grabbing">

      {/* Accent left border */}
      <div className="absolute left-0 top-0 bottom-0 w-[4px] rounded-l-md" style={{ background: meta.color }} />

      <header className="flex items-start justify-between gap-2 min-w-0 pl-1">
        <div className="min-w-0 flex-1">
          <p className={`truncate text-[13px] font-bold uppercase leading-tight ${emergency ? 'text-rose-600' : 'text-[#0d0d0d]'}`}>
            {emergency ? 'Emergency Transfer' : 'Stock Transfer'}
          </p>
        </div>

        <div className="relative shrink-0" ref={menuRef}>
          <button type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Transfer actions"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((open) => !open); }}
            className={`flex h-6 w-6 items-center justify-center rounded transition-colors ${
              menuOpen ? 'bg-slate-100 text-slate-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
            }`}>
            <MoreVertical size={16} />
          </button>

          {menuOpen && (
            <div role="menu"
              className="absolute right-0 top-full z-30 mt-1 flex w-40 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]">
              {cancelled ? (
                <>
                  <button type="button" role="menuitem" onClick={runAction(() => onReopen(transfer))}
                    className={`${menuItem} text-slate-700 hover:bg-slate-50`}>
                    <RotateCcw size={12} className="shrink-0 text-slate-400" />
                    <span className="truncate">Reopen</span>
                  </button>
                  <button type="button" role="menuitem" onClick={runAction(() => onDelete(transfer))}
                    className={`${menuItem} border-t border-slate-100 text-rose-600 hover:bg-rose-50`}>
                    <Trash2 size={12} className="shrink-0 text-rose-400" />
                    <span className="truncate">Delete</span>
                  </button>
                </>
              ) : (
                <>
                  <button type="button" role="menuitem" onClick={runAction(() => onView(transfer))}
                    className={`${menuItem} text-slate-700 hover:bg-slate-50`}>
                    <Package size={12} className="shrink-0 text-slate-400" />
                    <span className="truncate">View details</span>
                  </button>
                  {next && (
                    <button type="button" role="menuitem" onClick={runAction(() => onAdvance(transfer))}
                      className={`${menuItem} border-t border-slate-100 text-slate-700 hover:bg-slate-50`}>
                      <ArrowRight size={12} className="shrink-0" style={{ color: accent }} />
                      <span className="truncate">Move to {TRANSFER_STAGE_META[next].label}</span>
                    </button>
                  )}
                  <button type="button" role="menuitem" onClick={runAction(() => onCarrier(transfer))}
                    className={`${menuItem} border-t border-slate-100 text-slate-700 hover:bg-slate-50`}>
                    <TruckIcon size={12} className="shrink-0 text-slate-400" />
                    <span className="truncate">Assign Carrier</span>
                  </button>
                  <button type="button" role="menuitem" onClick={runAction(() => onCancel(transfer))}
                    className={`${menuItem} border-t border-slate-100 text-rose-600 hover:bg-rose-50`}>
                    <IconX size={12} className="shrink-0 text-rose-500" />
                    <span className="truncate">Cancel transfer</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Where the stock is moving: one line, in capitals.

          It was two spans and an arrow icon in a flex row, and that is what cut
          both names to an ellipsis — flex shares the overflow between its
          children, so each location was squeezed to half a card barely two
          hundred pixels wide however short the name actually was. As a single
          string there is nothing to share: the line uses the full width and
          clips only at the very end, which a real location name does not reach
          at this size. */}
      <div className="min-w-0 pl-1">
        <FieldLabel>Route</FieldLabel>
        <p className="truncate text-[10px] font-bold uppercase text-[#0d0d0d]"
          title={`${locationLabelOf(transfer.fromLocationId)} → ${locationLabelOf(transfer.toLocationId)}`}>
          {locationLabelOf(transfer.fromLocationId)}
          <span className="px-1 text-slate-400">→</span>
          {locationLabelOf(transfer.toLocationId)}
        </p>
      </div>

      <div className="min-w-0 pl-1">
        <FieldLabel>Carrier</FieldLabel>
        {carrier ? (
          <p className="truncate text-[11.5px] font-bold uppercase text-[#0d0d0d]"
            title={transfer.vehicle ? `${carrier} · ${transfer.vehicle}` : carrier}>
            {carrier}{transfer.vehicle ? ` · ${transfer.vehicle}` : ''}
          </p>
        ) : (
          <p className="truncate text-[11.5px] font-bold uppercase text-amber-600">Not assigned</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-2 border-t border-slate-100 pl-1 pr-1 pt-2">
        <div className="min-w-0">
          <FieldLabel>Items</FieldLabel>
          <p className="truncate text-[11.5px] font-bold uppercase text-[#0d0d0d]">
            {units} unit{units === 1 ? '' : 's'}
          </p>
        </div>
        <div className="min-w-0">
          <FieldLabel>{stamp.label}</FieldLabel>
          <p className={`truncate text-[11.5px] font-bold uppercase ${stamp.muted ? 'text-slate-500' : 'text-[#0d0d0d]'}`}
            title={stamp.value ? stamp.value.full : undefined}>
            {stamp.value ? stamp.value.short : '—'}
          </p>
        </div>
      </div>
    </motion.article>
  );
};

TransferCard.propTypes = {
  transfer: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  onAdvance: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCarrier: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ---- Transport tab ----
// direction: 'all' (default) | 'received' (only incoming to the scope location,
// hides "New transfer") | 'dispatch' (only outgoing from the scope location,
// pre-fills the new-transfer source).
export const TransportTab = ({ org, showNotification, lockedBranchId, direction = 'all' }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  const [search, setSearch] = useState('');
  const [locationFilters, setLocationFilters] = useState({});
  const [carrierTarget, setCarrierTarget] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const { lastMessage } = useWebSocket(org.id);

  useEffect(() => {
    if (lastMessage?.type === 'TRANSPORT_UPDATED' || lastMessage?.type === 'EMERGENCY_REQUEST') {
      refreshTransport(org.id);
      bump(); // Force re-render just in case
    }
  }, [lastMessage, org.id]);

  const accent = org.theme.primary;
  const locations = useMemo(() => stockLocationsOf(org.id), [org.id, dataVersion]);
  const locationsById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations, dataVersion]);
  const enoughLocations = locations.length >= 2;
  
  const scopeLocations = useMemo(() => {
    if (lockedBranchId) return [lockedBranchId];
    return [...(locationFilters.warehouse || []), ...(locationFilters.branch || [])];
  }, [lockedBranchId, locationFilters]);

  const filterCategories = useMemo(() => [
    {
      id: 'warehouse',
      label: 'Warehouses',
      type: 'multi',
      options: locations.filter(l => l.kind === 'warehouse').map(l => ({ id: l.id, label: l.name }))
    },
    {
      id: 'branch',
      label: 'Branches',
      type: 'multi',
      options: locations.filter(l => l.kind === 'branch').map(l => ({ id: l.id, label: l.name }))
    }
  ].filter(c => c.options.length > 0), [locations]);
  const canCreate = direction !== 'received';

  // Which routed flows are available to this org. Branch→Branch always
  // applies; the two warehouse-anchored flows only when the warehouse module
  // is on for this tenant.
  const warehouseEnabled = isModuleOn(org, 'warehouse');
  const availableRoutes = useMemo(
    () => ROUTE_OPTIONS.filter((r) => warehouseEnabled || !r.needsWarehouse),
    [warehouseEnabled],
  );
  const [routeMode, setRouteMode] = useState(availableRoutes[0]?.id || 'branch-to-branch');
  useEffect(() => {
    if (!availableRoutes.find((r) => r.id === routeMode)) {
      setRouteMode(availableRoutes[0]?.id || 'branch-to-branch');
    }
  }, [availableRoutes, routeMode, dataVersion]);

  const [dragOverStage, setDragOverStage] = useState(null);
  const selectedRoute = ROUTE_OPTIONS.find((r) => r.id === routeMode);

  const list = useMemo(() => {
    let rows = stockTransfersOf(org.id);
    if (scopeLocations && scopeLocations.length > 0) {
      if (direction === 'received') {
        rows = rows.filter((t) => scopeLocations.includes(t.toLocationId));
      } else if (direction === 'dispatch') {
        rows = rows.filter((t) => scopeLocations.includes(t.fromLocationId));
      } else {
        rows = rows.filter((t) => scopeLocations.includes(t.fromLocationId) || scopeLocations.includes(t.toLocationId));
      }
    }
    // Route filter — only keep transfers that match the selected flow.
    rows = rows.filter((t) => routeOf(t, locationsById) === routeMode);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((t) => {
        const text = `${t.id} ${t.carrier} ${t.note} ${locationLabelOf(t.fromLocationId)} ${locationLabelOf(t.toLocationId)} ${t.lines.map((l) => l.name).join(' ')}`;
        return text.toLowerCase().includes(q);
      });
    }
    return rows;
  }, [org.id, scopeLocations, direction, search, version, routeMode, locationsById, dataVersion]);

  // Locations the new-transfer modal allows on each side, given the route.
  const { fromLocations, toLocations } = useMemo(() => {
    const branches = locations.filter((l) => l.kind === 'branch');
    const warehouses = locations.filter((l) => l.kind === 'warehouse');
    switch (routeMode) {
      case 'wh-to-branch': return { fromLocations: warehouses, toLocations: branches };
      case 'branch-to-wh': return { fromLocations: branches, toLocations: warehouses };
      case 'wh-to-wh': return { fromLocations: warehouses, toLocations: warehouses };
      case 'branch-to-branch': return { fromLocations: branches, toLocations: branches };
      default: return { fromLocations: [], toLocations: [] };
    }
  }, [locations, routeMode, dataVersion]);
  const canCreateRoute = fromLocations.length > 0 && toLocations.length > ((routeMode === 'wh-to-wh' || routeMode === 'branch-to-branch') ? 1 : 0);

  const byStage = useMemo(() => {
    const map = {};
    TRANSFER_STAGES.forEach((s) => { map[s] = []; });
    map.cancelled = [];
    list.forEach((t) => { (map[t.status] || map.requested).push(t); });
    return map;
  }, [list, dataVersion]);

  const advance = (t) => {
    if (!t.carrier) {
      if (showNotification) showNotification(`Assign a carrier to move ${transferLabel(t)}.`, 'error');
      return;
    }
    advanceTransfer(t.id);
    const next = nextTransferStage(t.status);
    if (showNotification && next) showNotification(`${transferLabel(t)} moved to ${TRANSFER_STAGE_META[next].label}.`, 'success');
    bump();
  };
  const cancel = (t) => {
    updateStockTransfer(t.id, { status: 'cancelled' });
    if (showNotification) showNotification(`${transferLabel(t)} cancelled.`);
    bump();
  };
  const reopen = (t) => {
    updateStockTransfer(t.id, { status: 'requested' });
    if (showNotification) showNotification(`${transferLabel(t)} reopened.`, 'success');
    bump();
  };
  const removeRow = (t) => {
    deleteStockTransfer(t.id);
    if (showNotification) showNotification('Transfer removed.', 'success');
    bump();
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = e.dataTransfer.getData('transferId');
    if (!id) return;
    const transfer = list.find(t => t.id === id);
    if (!transfer || transfer.status === targetStage || targetStage === 'cancelled') return;
    
    if (!transfer.carrier) {
      if (showNotification) showNotification(`Assign a carrier to move ${transferLabel(transfer)}.`, 'error');
      return;
    }
    
    updateStockTransfer(id, { status: targetStage });
    if (showNotification) showNotification(`${transferLabel(transfer)} moved to ${TRANSFER_STAGE_META[targetStage].label}.`, 'success');
    bump();
  };
  const saveCarrier = (patch) => {
    if (!carrierTarget) return;
    updateStockTransfer(carrierTarget.id, patch);
    if (showNotification) showNotification(patch.carrier ? `Carrier set for ${transferLabel(carrierTarget)}.` : 'Carrier cleared.', 'success');
    setCarrierTarget(null);
    bump();
  };
  const createTransfer = (record) => {
    addStockTransfer(record);
    if (showNotification) showNotification('Stock transfer created.', 'success');
    setNewOpen(false);
    bump();
  };

  const newDefaultFrom = lockedBranchId || scopeLocations[0] || locations[0]?.id;

  return (
    <div className="flex flex-col gap-4 min-w-0 w-full">
      {/* Toolbar */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transfers…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
          </div>
          {lockedBranchId ? (
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-[11px] font-bold text-[#0d0d0d]" style={{ borderColor: 'var(--theme-tertiary)' }}>
              {locationLabelOf(lockedBranchId)}
            </span>
          ) : enoughLocations ? (
            <div className="relative inline-flex items-center">
              <FilterPopover
                categories={filterCategories}
                initialFilters={locationFilters}
                onApply={setLocationFilters}
                accent={accent}
                align="right"
              />
            </div>
          ) : null}
          {canCreate && (
            <button type="button" onClick={() => setNewOpen(true)} disabled={!enoughLocations || !canCreateRoute}
              title={!canCreateRoute ? `${selectedRoute?.label} needs at least one source and one destination of the right kind` : undefined}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg disabled:opacity-40 disabled:shadow-none whitespace-nowrap shrink-0"
              style={{ background: accent, boxShadow: enoughLocations && canCreateRoute ? `0 12px 24px -12px ${accent}` : 'none' }}>
              <Plus size={14} aria-hidden="true" /> New transfer
            </button>
          )}
        </div>
      </section>

      {/* Route switcher — visible only when 2+ routes apply to this org */}
      {availableRoutes.length > 1 && (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between flex-wrap">
          {/* Smaller type and tighter letter-spacing than the app's usual tab
              style. These labels are two words and an arrow rather than one
              word, so at 12px with 0.14em of tracking four of them wrapped onto
              a second row on anything narrower than a desktop.

              `uppercase` is repeated on each button rather than left to inherit
              from here. It was set only on this container and the buttons were
              still rendering "Warehouse → Warehouse" in sentence case, so the
              inheritance was not surviving whatever sits between. Stating it on
              the element that draws the text removes the question. */}
          <div role="tablist" aria-label="Transfer route" className="flex flex-wrap items-center gap-0.5 bg-[var(--theme-secondary)] rounded-lg p-1 text-[10px] font-semibold uppercase tracking-[0.06em]">
            {availableRoutes.map((r) => {
              const active = routeMode === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRouteMode(r.id)}
                  className={`px-2.5 py-1 rounded-lg uppercase transition-colors whitespace-nowrap ${active ? 'text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  style={active ? { background: accent } : undefined}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          {selectedRoute && (
            <p className="text-[11px] font-medium text-slate-500 min-w-0">{selectedRoute.desc}</p>
          )}
        </section>
      )}

      {!enoughLocations ? (
        <div className="py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
          <ArrowLeftRight size={28} className="mx-auto text-slate-300" aria-hidden="true" />
          <p className="mt-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">Transfers need 2+ stock locations.</p>
          <p className="text-[11px] font-medium text-slate-400 mt-1">Add a branch or enable the central warehouse to move stock.</p>
        </div>
      ) : (
        <>

          {/* Pipeline board */}
          <section className="flex flex-col md:flex-row gap-4 overflow-x-auto overflow-y-hidden pb-6 pt-2 w-full min-w-0 custom-scrollbar">
            {TRANSFER_STAGES.map((stage) => {
              const meta = TRANSFER_STAGE_META[stage];
              const cards = byStage[stage];
              return (
                <div 
                  key={stage} 
                  className="flex flex-col gap-3 shrink-0 w-72 lg:flex-1 lg:w-auto" 
                  style={{ minWidth: '210px' }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage); }}
                  onDragLeave={(e) => { e.preventDefault(); if (dragOverStage === stage) setDragOverStage(null); }}
                  onDrop={(e) => handleDrop(e, stage)}
                >
                  <header className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} aria-hidden="true" />
                      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">{meta.label}</h3>
                    </div>
                    <span className="text-[11px] font-semibold tabular-nums" style={{ color: meta.color }}>
                      {cards.length}
                    </span>
                  </header>
                  <div className={`flex flex-col gap-3 min-h-[80px] rounded-lg border border-dashed p-2.5 flex-1 transition-colors ${dragOverStage === stage ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)]' : 'border-[var(--theme-tertiary)]'}`}>
                    <AnimatePresence>
                      {cards.map((t) => (
                        <TransferCard
                          key={t.id}
                          transfer={t}
                          accent={accent}
                          onAdvance={advance}
                          onCancel={cancel}
                          onCarrier={setCarrierTarget}
                          onReopen={reopen}
                          onDelete={removeRow}
                          onView={setViewTarget}
                        />
                      ))}
                    </AnimatePresence>
                    {cards.length === 0 && (
                      <p className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">{meta.hint}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Cancelled */}
          {byStage.cancelled.length > 0 && (
            <section className="flex flex-col gap-3">
              <header className="flex items-center gap-2 px-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: TRANSFER_STAGE_META.cancelled.color }} aria-hidden="true" />
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">Cancelled</h3>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-lg" style={{ background: `${TRANSFER_STAGE_META.cancelled.color}14`, color: TRANSFER_STAGE_META.cancelled.color }}>
                  {byStage.cancelled.length}
                </span>
              </header>
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-start">
                <AnimatePresence>
                  {byStage.cancelled.map((t) => (
                    <TransferCard
                      key={t.id}
                      transfer={t}
                      accent={accent}
                      onAdvance={advance}
                      onCancel={cancel}
                      onCarrier={setCarrierTarget}
                      onReopen={reopen}
                      onDelete={removeRow}
                      onView={setViewTarget}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}

          {list.length === 0 && (
            <div className="py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
              <ArrowLeftRight size={28} className="mx-auto text-slate-300" aria-hidden="true" />
              <p className="mt-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No transfers yet.</p>
              <button type="button" onClick={() => setNewOpen(true)} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                <Plus size={12} /> Raise a transfer
              </button>
            </div>
          )}
        </>
      )}

      {carrierTarget && (
        <CarrierModal
          transfer={carrierTarget}
          accent={accent}
          onClose={() => setCarrierTarget(null)}
          onSave={saveCarrier}
        />
      )}
      {newOpen && (
        <NewTransferModal
          org={org}
          fromLocations={fromLocations}
          toLocations={toLocations}
          defaultFromId={newDefaultFrom}
          accent={accent}
          onClose={() => setNewOpen(false)}
          onCreate={createTransfer}
        />
      )}
      {viewTarget && (
        <ViewTransferModal
          transfer={viewTarget}
          accent={accent}
          onClose={() => setViewTarget(null)}
        />
      )}
    </div>
  );
};

TransportTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  lockedBranchId: PropTypes.string,
  direction: PropTypes.oneOf(['all', 'received', 'dispatch']),
};
